/**
 * One member's record.
 *
 * Two things are easy to get wrong here and both mislead in the same
 * direction — they make someone look worse than the record says.
 *
 * Attendance is shown as votes cast against rolls held, because "missed 40"
 * means nothing without the denominator; forty out of two thousand is a good
 * record and forty out of sixty is not.
 *
 * The party figure is shown with its own denominator too, and rolls where
 * the rest of their party split evenly are excluded from it — there was no
 * line to cross — so it is smaller than the number of votes they cast.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, type MemberVote, type VoteMoneySummary } from "../api";
import { useAsync } from "../useAsync";
import { Affiliation, Avatar } from "../components/MemberCard";
import { Money } from "../components/Money";
import { MemberRevolvingDoor } from "../components/RevolvingDoor";
import { VoteMoneyCell, VoteMoneyNote, VoteMoneyPanel } from "../components/VoteMoney";
import { loyalty } from "../components/party";
import { Problem, Spinner } from "../components/bits";

const POSITION: Record<string, string> = {
  Yea: "bg-emerald-50 text-emerald-800",
  Nay: "bg-rose-50 text-rose-800",
  Present: "bg-sunk text-muted",
  "Not Voting": "bg-sunk text-faint",
};

export function MemberPage() {
  const { bioguide = "" } = useParams();
  const found = useAsync(() => api.member(bioguide), [bioguide]);
  // A second request rather than a key on each vote in the first. It is the
  // same window as the voting record, so the two line up, and a member page
  // still renders whole if this one fails — the record is the page, and the
  // money beside it is an addition to it.
  const money = useAsync(() => api.memberVoteMoney(bioguide), [bioguide]);

  if (found.loading && !found.data) return <Spinner label="Reading the record…" />;
  if (found.error) {
    // A 404 is not a failure to retry. "Try again" on a bioguide that does
    // not exist offers the reader a button that cannot work, so a bad id
    // gets a way out instead of a way to repeat itself.
    const missing = found.error instanceof ApiError && found.error.status === 404;
    return (
      <div className="mx-auto max-w-2xl px-5 py-16">
        {missing ? (
          <div className="rounded-2xl border border-line bg-card p-10 text-center">
            <p className="font-display text-xl font-semibold">No member with that id.</p>
            <p className="mt-2 text-muted">
              This covers the Congresses in the corpus, so someone who left before then will not be
              here.
            </p>
          </div>
        ) : (
          <Problem error={found.error} retry={found.reload} />
        )}
        <p className="mt-6 text-center">
          <Link to="/members" className="font-medium text-brand hover:underline">
            ← Everyone in Congress
          </Link>
        </p>
      </div>
    );
  }
  if (!found.data) return null;

  const { record, votes, sponsored } = found.data;
  const turnout = record.votes + record.missed;

  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      <p className="mb-6 text-sm">
        <Link to="/members" className="text-muted hover:text-ink">
          ← Everyone in Congress
        </Link>
      </p>

      <header className="flex flex-wrap items-center gap-5">
        <Avatar
          bioguide={record.bioguide}
          name={record.full_name}
          party={record.party}
          size={96}
        />
        <div>
          <h1 className="font-display text-3xl font-semibold">
            {record.full_name ?? record.bioguide}
          </h1>
          <p className="mt-1">
            <Affiliation member={record} />
          </p>
        </div>
      </header>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          value={loyalty(record.with_party)}
          label="voted with their party"
          note={
            record.with_party.votable
              ? `on ${record.with_party.votable.toLocaleString()} rolls where their party took a side`
              : "no roll where their party took a side"
          }
        />
        <Stat
          value={record.votes.toLocaleString()}
          label="votes cast"
          note={turnout ? `of ${turnout.toLocaleString()} they were called on` : "none recorded"}
        />
        <Stat
          value={record.sponsored.toLocaleString()}
          label="bills sponsored"
          note={`${record.cosponsored.toLocaleString()} co-sponsored`}
        />
        <Stat
          value={record.laws.toLocaleString()}
          label={record.laws === 1 ? "became law" : "became law"}
          note="of the ones they led on"
        />
      </div>

      {/* Above the voting record on purpose. Who paid for the seat is
          context for how it was used, and a reader who scrolls past sixty
          roll calls first will never reach it. */}
      <Money bioguide={record.bioguide} />

      {/* Directly under the money, because it is the only tie to this member
          that is not money and reads as an answer to the section above it.
          Most members get nothing here: a declaration naming them is
          something 534 of them have and the rest do not. */}
      <MemberRevolvingDoor bioguide={record.bioguide} />

      <Section title="How they voted">
        {/* Before the list, not after it. The note is what stops a figure
            beside a roll being read as a payment for that roll, and a
            reader who meets sixty rolls first has already read them
            wrongly. */}
        {money.data && (
          <VoteMoneyNote
            coverage={money.data.coverage}
            found={Object.keys(money.data.votes).length}
          />
        )}
        {votes.length === 0 ? (
          <p className="mt-3 text-muted">No roll calls recorded for them here.</p>
        ) : (
          <ul className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
            {votes.slice(0, 60).map((vote) => (
              <VoteRow
                key={vote.vote_id}
                vote={vote}
                bioguide={record.bioguide}
                money={money.data?.votes[vote.vote_id]}
              />
            ))}
          </ul>
        )}
        {votes.length > 60 && (
          <p className="mt-3 text-sm text-faint">
            The 60 most recent of {votes.length.toLocaleString()} shown.
          </p>
        )}
      </Section>

      {sponsored.length > 0 && (
        <Section title="Bills they led on">
          <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
            {sponsored.slice(0, 25).map((bill) => (
              <li key={bill.bill_id} className="px-4 py-3">
                {bill.law_number ? (
                  <Link
                    to={`/law/${bill.bill_id}`}
                    className="font-medium text-brand hover:underline"
                  >
                    {bill.title}
                  </Link>
                ) : (
                  <span>{bill.title}</span>
                )}
                {bill.law_number && (
                  <span className="tnum ml-2 text-xs font-semibold tracking-wide text-brand uppercase">
                    Public Law {bill.law_number}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      <p className="mt-12 border-t border-line pt-6 text-sm text-muted">
        Party is taken from the vote as it was cast, not from where they sit today, so someone who
        changed party is measured against the party they were in at the time.
      </p>
    </div>
  );
}

function Stat({ value, label, note }: { value: string; label: string; note: string }) {
  return (
    <div className="rounded-2xl border border-line bg-card p-4">
      <p className="tnum font-display text-2xl font-semibold">{value}</p>
      <p className="mt-0.5 text-sm font-medium">{label}</p>
      <p className="mt-1 text-xs leading-relaxed text-faint">{note}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * One roll.
 *
 * Most belong to no bill — procedural motions and quorum calls — and those
 * still show the member turning up, so the question is the label when there
 * is no title.
 *
 * Only enacted bills are linked. Having a `bill_id` is not enough: the great
 * majority of these rolls are cloture motions, nominations and bills that
 * died, none of which have a page, so linking on `bill_id` alone sends most
 * of this list to a 404.
 *
 * The money on the right is a button rather than a link, and it is only
 * there when there is something. Most rolls have nothing, and the ones that
 * do are the reason anybody scrolls this far.
 */
function VoteRow({
  vote,
  bioguide,
  money,
}: {
  vote: MemberVote;
  bioguide: string;
  money?: VoteMoneySummary;
}) {
  const [open, setOpen] = useState(false);
  const label = vote.title ?? vote.question ?? "Roll call";
  const tone = POSITION[vote.position] ?? "bg-sunk text-muted";
  const readable = vote.bill_id && vote.law_number;

  return (
    <li>
      <div className="flex items-start gap-3 px-4 py-3">
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>
          {vote.position}
        </span>
        <span className="min-w-0 flex-1">
          {readable ? (
            <Link to={`/law/${vote.bill_id}`} className="font-medium text-brand hover:underline">
              {label}
            </Link>
          ) : (
            <span className="font-medium">{label}</span>
          )}
          <span className="block text-xs text-faint">
            {vote.date}
            {vote.chamber && <> · {vote.chamber === "house" ? "House" : "Senate"}</>}
            {vote.result && <> · {vote.result}</>}
            {vote.law_number && <> · became Public Law {vote.law_number}</>}
          </span>
        </span>
        {money && (
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-expanded={open}
            className="shrink-0 rounded-lg px-1.5 py-0.5 text-right hover:bg-sunk"
            title="Who lobbied this bill and gave this member money"
          >
            <VoteMoneyCell found={money} />
          </button>
        )}
      </div>
      {open && <VoteMoneyPanel bioguide={bioguide} voteId={vote.vote_id} />}
    </li>
  );
}
